/**
 * DeliveryOrderFormDialog — New / Edit / Copy for Sales Control › Delivery Order.
 * Converted 1:1 from Access `frmCtrlSalesQryDOAdd`: 5 blocks (bold gutter header
 * Left=72) — Delivery order information · ICO - Marking · Service · Booking for
 * export · Docs Sending — mỗi block 1 grid 3 cột (Left 3816/10200/16080 → 1/2/3),
 * row theo Top. Combos = Autocomplete (store code, display name). Required:
 * do_no_index + sale_contract (Access btnSave errCheck 2/3). Cascades:
 *   do_no_index → do_no (=`${index}TNC`)
 *   ico_no_index → ico_no (=`145/19743/${index}`)
 *   sale_contract → tode_delivery (contract qty − delivered)
 *   customs → customs_name ; sending_docs_to → indication (first 2 chars)
 */
import { type ChangeEvent, useEffect, useState } from 'react'
import { TEXT_TRUNCATE } from '../../theme/responsive'
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogContent,
  DialogTitle, Stack, TextField, Typography, createFilterOptions,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'

import {
  type DoLookupItem,
  type SaleDeliveryOrderWrite,
  useCreateSaleDeliveryOrderMutation,
  useUpdateSaleDeliveryOrderMutation,
  useLazyGetSaleDeliveryOrderFormQuery,
  useLazySuggestDoNoQuery,
  useLazySuggestIcoNoQuery,
  useLazyCheckDoNoQuery,
  useLazyGetDoSnapshotQuery,
  useDoLookupSaleContractsQuery,
  useDoLookupWarehousesQuery,
  useDoLookupDunnageQuery,
  useDoLookupInspectionTypesQuery,
  useDoLookupServicesQuery,
  useDoLookupDocsDispatchQuery,
  useDoLookupPortsQuery,
  useDoLookupVesselsQuery,
  useDoLookupExportTerminalsQuery,
  useDoLookupInternalPortsQuery,
  useDoLookupPicsQuery,
} from '../../store/api/saleControlApi'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'

type Mode = 'new' | 'edit' | 'copy'

interface Props {
  open: boolean
  mode: Mode
  doId?: number | null
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

interface Draft {
  doNoIndex: string
  doNo: string
  saleContract: string
  warehouseType: string
  fromWarehouse: string
  doDate: string
  loadingDate: string
  nwKgs: string
  icoNoIndex: string
  icoNo: string
  marking: string
  dunnage: string
  typeOfInspection: string
  fumiDose: string
  noContTruck: string
  inspectionCompany: string
  fumigationCompany: string
  truckCompany: string
  shippingLine: string
  freightPayable: string
  customs: string
  customsName: string
  bookingNo: string
  bookingNote: string
  blNumber: string
  vesselName: string
  voyage: string
  exportTerminal: string
  portOfLoading: string
  transhipmentPort: string
  destinationPort: string
  internalTransshipmentPort: string
  placeOfDelivery: string
  cutOff: string
  etd: string
  eta: string
  atd: string
  docsPresentationDate: string
  sendingDocsTo: string
  indication: string
  pic: string
  todeDelivery: string
}

const EMPTY: Draft = {
  doNoIndex: '', doNo: '', saleContract: '', warehouseType: '', fromWarehouse: '',
  doDate: '', loadingDate: '', nwKgs: '', icoNoIndex: '', icoNo: '', marking: '',
  dunnage: '', typeOfInspection: '', fumiDose: '', noContTruck: '', inspectionCompany: '',
  fumigationCompany: '', truckCompany: '', shippingLine: '', freightPayable: '', customs: '',
  customsName: '', bookingNo: '', bookingNote: '', blNumber: '', vesselName: '', voyage: '',
  exportTerminal: '', portOfLoading: '', transhipmentPort: '', destinationPort: '',
  internalTransshipmentPort: '', placeOfDelivery: '', cutOff: '', etd: '', eta: '', atd: '',
  docsPresentationDate: '', sendingDocsTo: '', indication: '', pic: '', todeDelivery: '',
}

const SM = 'small' as const
// Compact input sizing — đồng bộ với các form convert-form khác (vd factory
// DeliveryFormDialog): 0.8125rem cho input + label, minHeight 32. Áp lên
// DialogContent để mọi TextField/Autocomplete/DatePicker con dùng cùng cỡ chữ.
const compactInputSx: SxProps<Theme> = {
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
}
const titleBtnSx: SxProps<Theme> = { minWidth: 0, px: 1.25, py: 0.3, fontSize: '0.78rem', textTransform: 'none' }
// Block header (Access bold gutter label Left=72) + 3-col grid per block.
const sectionTitleSx: SxProps<Theme> = {
  fontWeight: 700, fontSize: '0.8125rem', color: 'primary.main',
  mt: 1.5, mb: 0.75, pb: 0.25, borderBottom: '1px solid', borderColor: 'divider',
}
const grid3Sx: SxProps<Theme> = {
  display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' },
  columnGap: 1.5, rowGap: 1.25, alignItems: 'start',
}
const readonlyFieldSx = {
  '& .MuiInputBase-root': { bgcolor: (t: Theme) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5') },
  '& .MuiInputBase-input': { color: (t: Theme) => t.palette.text.secondary, fontWeight: 600 },
}
const numProps = { style: { textAlign: 'right' as const }, inputMode: 'decimal' as const }
// Access multi-row fields (Height > 312): match cell height deterministically across
// TextBox + ComboBox in the SAME grid row. MUI Autocomplete ignores multiline/minRows,
// so we force an explicit minHeight (≈ rows × 26px) + top-align on the input root.
// Áp cho MỌI field cùng dòng kẻo lệch (form dùng CSS Grid). 1 dòng = Access Height 312.
const tallFieldSx = (minRows: number): SxProps<Theme> => ({
  '& .MuiInputBase-root': { minHeight: minRows * 26, alignItems: 'flex-start' },
})

function parseNum(v: string): number | null {
  const s = (v ?? '').toString().replace(/,/g, '').trim()
  if (!s) return null
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}
/** Access Format="Standard" (nw_kgs / no_cont_truck / tode_delivery): luôn 2 thập phân
 *  + phân cách nghìn (`#,##0.00`), căn phải. */
function fmtStd(v: unknown): string {
  if (v == null || v === '') return ''
  const n = typeof v === 'number' ? v : parseNum(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
const s = (v: unknown): string => (v == null ? '' : String(v))

/** Inject the current value into options so the chip shows even before list load / when filtered out. */
function withCurrent(options: DoLookupItem[], value: string, label?: string): DoLookupItem | null {
  if (!value) return null
  return options.find((o) => o.value === value) ?? { value, label: label || value }
}

// Client-side filter theo chuỗi đầy đủ "<code> — <name>" — combo codeOnly có
// getOptionLabel chỉ trả code nên MUI default filter sẽ KHÔNG match được theo tên.
// (Combo server-search giữ `(x) => x`; BE đã lọc q theo cả code + name.)
const comboFilter = createFilterOptions<DoLookupItem>({
  stringify: (o) => `${String(o.value ?? '')} — ${String(o.label ?? '')}`,
  trim: true,
})

/* ───────── Reusable combos ───────── */
function Combo(props: {
  label: string
  value: string
  valueLabel?: string
  options: DoLookupItem[]
  loading?: boolean
  required?: boolean
  labelOnly?: boolean
  /** Field có ô name riêng kế bên (vd Customs Code ↔ Customs Name) → field combo
   *  chỉ hiện CODE; dropdown vẫn "<code> — <name>". */
  codeOnly?: boolean
  minRows?: number
  onInputChange?: (q: string) => void
  onPick: (value: string, item: DoLookupItem | null) => void
  error?: boolean
  helperText?: string
}) {
  const { label, value, valueLabel, options, loading, required, labelOnly, codeOnly, minRows, onInputChange, onPick, error, helperText } = props
  const sel = withCurrent(options, value, valueLabel)
  // Inject current value INTO options (not just value prop) — server-side
  // limit/filter/search lookups thường không chứa value đã lưu → MUI warn
  // "none of the options match". isOptionEqualToValue KHÔNG đủ; phải có trong options.
  const opts = sel && !options.some((o) => o.value === sel.value) ? [sel, ...options] : options
  const fullLabel = (o: DoLookupItem) =>
    !o ? '' : labelOnly || o.value === o.label ? String(o.label ?? '') : `${o.value} — ${o.label}`
  const getLabel = (o: DoLookupItem) => (!o ? '' : codeOnly ? String(o.value) : fullLabel(o))
  return (
    <Autocomplete
      size={SM}
      options={opts}
      value={sel}
      loading={loading}
      onChange={(_e, v) => onPick(v?.value ?? '', v)}
      onInputChange={onInputChange ? (_e, v, reason) => { if (reason === 'input') onInputChange(v) } : undefined}
      getOptionLabel={getLabel}
      renderOption={codeOnly ? (liProps, o) => <li {...liProps} key={o.value}>{fullLabel(o)}</li> : undefined}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      filterOptions={onInputChange ? (x) => x : comboFilter}
      renderInput={(params) => (
        <TextField
          {...params}
          label={required ? `${label} *` : label}
          error={error}
          helperText={helperText}
          sx={minRows ? tallFieldSx(minRows) : undefined}
        />
      )}
    />
  )
}

function ServiceCombo(props: {
  label: string; types: string; value: string; valueLabel?: string; minRows?: number; codeOnly?: boolean
  onPick: (v: string, item: DoLookupItem | null) => void
}) {
  const [q, setQ] = useState('')
  const { data, isFetching } = useDoLookupServicesQuery({ types: props.types, q: q || undefined })
  return (
    <Combo label={props.label} value={props.value} valueLabel={props.valueLabel} minRows={props.minRows}
      codeOnly={props.codeOnly}
      options={data?.items ?? []} loading={isFetching} onInputChange={setQ} onPick={props.onPick} />
  )
}

function PortCombo(props: {
  label: string; value: string; valueLabel?: string
  onPick: (v: string, item: DoLookupItem | null) => void
}) {
  const [q, setQ] = useState('')
  const { data, isFetching } = useDoLookupPortsQuery(q || undefined)
  return (
    <Combo label={props.label} value={props.value} valueLabel={props.valueLabel}
      options={data?.items ?? []} loading={isFetching} onInputChange={setQ} onPick={props.onPick} />
  )
}

const WAREHOUSE_TYPES: DoLookupItem[] = [{ value: 'Bonded', label: 'Bonded' }, { value: 'Non-bonded', label: 'Non-bonded' }]

export default function DeliveryOrderFormDialog({ open, mode, doId, canEdit, onClose, onSaved }: Props) {
  const snack = useSnackbar()
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [labels, setLabels] = useState<Record<string, string>>({})
  const [doNoExists, setDoNoExists] = useState(false)
  // Edit/Copy hydrate hits a ~2s view query → show a loading state until the
  // API response lands, then render the fields (avoid a flash of empty inputs).
  const [formLoading, setFormLoading] = useState(false)
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((p) => ({ ...p, [k]: v }))

  const [loadForm] = useLazyGetSaleDeliveryOrderFormQuery()
  const [suggestDo] = useLazySuggestDoNoQuery()
  const [suggestIco] = useLazySuggestIcoNoQuery()
  const [checkDo] = useLazyCheckDoNoQuery()

  const checkDoNo = (idx: string) => {
    const n = parseInt(idx, 10)
    if (!Number.isFinite(n)) { setDoNoExists(false); return }
    void checkDo({ index: n, excludeId: mode === 'edit' && doId ? doId : undefined })
      .unwrap().then((r) => setDoNoExists(r.exists)).catch(() => setDoNoExists(false))
  }
  const [fetchSnapshot] = useLazyGetDoSnapshotQuery()
  const [createDo, { isLoading: creating }] = useCreateSaleDeliveryOrderMutation()
  const [updateDo, { isLoading: updating }] = useUpdateSaleDeliveryOrderMutation()
  const saving = creating || updating

  // Static (small) lookups — load when dialog open
  const [scQ, setScQ] = useState('')
  const skip = { skip: !open }
  const saleContracts = useDoLookupSaleContractsQuery(scQ || undefined, skip)
  const warehouses = useDoLookupWarehousesQuery(undefined, skip)
  const dunnage = useDoLookupDunnageQuery(undefined, skip)
  const inspectionTypes = useDoLookupInspectionTypesQuery(undefined, skip)
  const docsDispatch = useDoLookupDocsDispatchQuery(undefined, skip)
  const vessels = useDoLookupVesselsQuery(undefined, skip)
  const exportTerminals = useDoLookupExportTerminalsQuery(undefined, skip)
  const internalPorts = useDoLookupInternalPortsQuery(undefined, skip)
  const pics = useDoLookupPicsQuery(undefined, skip)

  // Hydrate on open
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setDoNoExists(false)
    if (mode !== 'new') setFormLoading(true) // show loading immediately for Edit/Copy
    void (async () => {
      if (mode === 'new') {
        setFormLoading(false)
        const today = dayjs().format('YYYY-MM-DD')
        const [doIdx, icoIdx] = await Promise.all([suggestDo().unwrap().catch(() => null), suggestIco().unwrap().catch(() => null)])
        if (cancelled) return
        setLabels({})
        setDraft({
          ...EMPTY,
          doDate: today,
          doNoIndex: doIdx ? String(doIdx.index) : '',
          doNo: doIdx ? `${doIdx.index}TNC` : '',
          icoNoIndex: icoIdx ? String(icoIdx.index) : '',
          icoNo: icoIdx ? `145/19743/${icoIdx.index}` : '',
        })
        return
      }
      if (!doId) { setFormLoading(false); return }
      const f = await loadForm(doId).unwrap().catch(() => null)
      if (cancelled) return
      if (!f) { setFormLoading(false); return }
      const g = (k: string) => s((f as Record<string, unknown>)[k])
      const isCopy = mode === 'copy'
      // Copy: new do_no index + today; Edit: keep stored do_no.
      let doNoIndex = ''
      let doNo = ''
      if (isCopy) {
        const idx = await suggestDo().unwrap().catch(() => null)
        doNoIndex = idx ? String(idx.index) : ''
        doNo = idx ? `${idx.index}TNC` : ''
      } else {
        doNo = g('doNo')
        doNoIndex = doNo ? doNo.replace(/...$/, '') : ''
      }
      const icoNo = g('icoNo')
      const icoNoIndex = icoNo.includes('/') ? icoNo.split('/')[2] ?? '' : ''
      setLabels({
        inspectionCompany: g('inspectionName'), fumigationCompany: g('fumigationName'),
        truckCompany: g('truckName'), shippingLine: g('shippingLinesName'),
        customs: g('customsName'), portOfLoading: g('portOfLoadingName'),
        transhipmentPort: g('transhipmentPortName'), destinationPort: g('destinationPortName'),
        sendingDocsTo: g('docsReceiverFullname'), pic: g('fullName'),
      })
      setDraft({
        ...EMPTY,
        doNoIndex, doNo,
        saleContract: g('saleContract'), warehouseType: g('warehouseType'), fromWarehouse: g('fromWarehouse'),
        doDate: isCopy ? dayjs().format('YYYY-MM-DD') : g('doDate').slice(0, 10),
        loadingDate: g('loadingDate').slice(0, 10),
        nwKgs: fmtStd(g('nwKgs')), icoNoIndex, icoNo, marking: g('marking'), dunnage: g('dunnage'),
        typeOfInspection: g('typeOfInspection'), fumiDose: g('fumiDose'), noContTruck: fmtStd(g('noContTruck')),
        inspectionCompany: g('inspectionCompany'), fumigationCompany: g('fumigationCompany'),
        truckCompany: g('truckCompany'), shippingLine: g('shippingLine'), freightPayable: g('freightPayable'),
        customs: g('customs'), customsName: g('customsName'), bookingNo: g('bookingNo'), bookingNote: g('bookingNote'),
        blNumber: g('blNumber'), vesselName: g('vesselName'), voyage: g('voyage'), exportTerminal: g('exportTerminal'),
        portOfLoading: g('portOfLoading'), transhipmentPort: g('transhipmentPort'), destinationPort: g('destinationPort'),
        internalTransshipmentPort: g('internalTransshipmentPort'), placeOfDelivery: g('placeOfDelivery'),
        cutOff: g('cutOff'), etd: g('etd').slice(0, 10), eta: g('eta').slice(0, 10), atd: g('atd').slice(0, 10),
        docsPresentationDate: g('docsPresentationDate').slice(0, 10), sendingDocsTo: g('sendingDocsTo'),
        indication: g('indication'), pic: g('pic'), todeDelivery: '',
      })
      setFormLoading(false)
    })()
    return () => { cancelled = true }
  }, [open, mode, doId, loadForm, suggestDo, suggestIco])

  // sale_contract → tode_delivery snapshot
  useEffect(() => {
    if (!open || !draft.saleContract) return
    let cancelled = false
    void fetchSnapshot({ saleContract: draft.saleContract, excludeId: mode === 'edit' && doId ? doId : undefined })
      .unwrap()
      .then((r) => { if (!cancelled) set('todeDelivery', fmtStd(r.todeDelivery)) })
      .catch(() => undefined)
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, draft.saleContract, doId, mode])

  const doNoIndexEmpty = !draft.doNoIndex.trim()
  const saleContractEmpty = !draft.saleContract.trim()
  const canSave = canEdit && !doNoIndexEmpty && !saleContractEmpty && !doNoExists && !saving

  const handleClose = () => { if (!saving) onClose() }

  const handleSave = async () => {
    if (!canSave) return
    const payload: SaleDeliveryOrderWrite = {
      doNo: draft.doNo || `${draft.doNoIndex}TNC`,
      doDate: draft.doDate || null,
      warehouseType: draft.warehouseType || null,
      fromWarehouse: draft.fromWarehouse || null,
      saleContract: draft.saleContract || null,
      loadingDate: draft.loadingDate || null,
      netWeightKg: parseNum(draft.nwKgs),
      icoNo: draft.icoNo && draft.icoNo !== '145/19743/' ? draft.icoNo : null,
      marking: draft.marking || null,
      dunnage: draft.dunnage || null,
      typeOfInspection: draft.typeOfInspection || null,
      inspectionCompany: draft.inspectionCompany || null,
      fumiDose: draft.fumiDose || null,
      fumigationCompany: draft.fumigationCompany || null,
      noContTruck: parseNum(draft.noContTruck),
      truckCompany: draft.truckCompany || null,
      bookingNote: draft.bookingNote || null,
      bookingNo: draft.bookingNo || null,
      freightPayable: draft.freightPayable || null,
      blNumber: draft.blNumber || null,
      shippingLine: draft.shippingLine || null,
      vesselName: draft.vesselName || null,
      voyage: draft.voyage || null,
      exportTerminal: draft.exportTerminal || null,
      portOfLoading: draft.portOfLoading || null,
      transhipmentPort: draft.transhipmentPort || null,
      destinationPort: draft.destinationPort || null,
      placeOfDelivery: draft.placeOfDelivery || null,
      cutOff: draft.cutOff || null,
      etd: draft.etd || null,
      eta: draft.eta || null,
      atd: draft.atd || null,
      docsPresentationDate: draft.docsPresentationDate || null,
      documentReceiver: draft.sendingDocsTo || null,
      pic: draft.pic || null,
      customs: draft.customs || null,
      internalTransshipmentPort: draft.internalTransshipmentPort || null,
    }
    try {
      if (mode === 'edit' && doId) {
        await updateDo({ id: doId, body: payload }).unwrap()
        snack.success(`Delivery Order ${payload.doNo} updated`)
      } else {
        await createDo(payload).unwrap()
        snack.success(`Delivery Order ${payload.doNo} added`)
      }
      onSaved()
      onClose()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Save delivery order failed'))
    }
  }

  const title = mode === 'new' ? 'New' : mode === 'copy' ? 'Copy' : 'Edit'

  const dateField = (key: keyof Draft, label: string) => (
    <DatePicker
      label={label}
      format="DD-MMM-YYYY"
      value={draft[key] ? dayjs(draft[key] as string) : null}
      onChange={(d) => set(key, (d && d.isValid() ? d.format('YYYY-MM-DD') : '') as Draft[typeof key])}
      slotProps={{ textField: { size: SM, fullWidth: true, InputLabelProps: { shrink: true } } }}
    />
  )
  const numField = (key: keyof Draft, label: string) => (
    <TextField
      label={label} size={SM} fullWidth type="text" value={draft[key] as string}
      onChange={(e) => set(key, e.target.value as Draft[typeof key])}
      onBlur={() => set(key, fmtStd(draft[key] as string) as Draft[typeof key])}
      inputProps={numProps}
    />
  )
  const textField = (key: keyof Draft, label: string, extra?: object) => (
    <TextField label={label} size={SM} fullWidth value={draft[key] as string}
      onChange={(e) => set(key, e.target.value as Draft[typeof key])} {...extra} />
  )
  const roField = (key: keyof Draft, label: string, numeric?: boolean) => (
    <TextField label={label} size={SM} fullWidth value={draft[key] as string}
      InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={readonlyFieldSx}
      inputProps={numeric ? { style: { textAlign: 'right' } } : undefined} />
  )

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      fullWidth
      onClose={(_e, reason) => { if (saving || reason === 'backdropClick') return; handleClose() }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <Typography component="span" variant="subtitle1" noWrap title={`${title} — Delivery Order`} sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700 }}>{title} — Delivery Order</Typography>
        <Stack direction="row" spacing={0.75} sx={{ ml: 'auto' }}>
          <Button type="button" variant="contained" color="success" sx={titleBtnSx} onClick={() => void handleSave()} disabled={!canSave}>
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" sx={titleBtnSx} onClick={handleClose} disabled={saving}>Cancel</Button>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={compactInputSx}>
        {formLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 8 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">Loading…</Typography>
          </Box>
        ) : (
        <>
        {!canEdit && <Alert severity="info" sx={{ mb: 1.5 }}>You do not have permission to edit delivery orders.</Alert>}
        {/* 5 block theo Access frmCtrlSalesQryDOAdd (bold gutter header Left=72):
            Delivery order information · ICO - Marking · Service · Booking for export
            · Docs Sending. Mỗi block = 1 grid 3 cột (Access Left 3816/10200/16080
            → col 1/2/3), row theo Top trong block. Block header chia sẻ Top với row
            field đầu tiên của block. Computed code fields (export_terminal_code, port
            codes, company codes, email, service short_name) bị bỏ — chỉ là DLookup
            `.column(1)` của combo, FE đã hiển thị code trong "CODE — Name". */}

        {/* ── Block 1: Delivery order information (T=48–1200) ── */}
        <Typography sx={sectionTitleSx}>Delivery order information</Typography>
        <Box sx={grid3Sx}>
          <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 1 } }}>
            {textField('doNoIndex', 'DO No.', {
              required: true,
              error: doNoIndexEmpty || doNoExists,
              helperText: doNoExists ? 'This DO No. already exists' : undefined,
              onChange: (e: ChangeEvent<HTMLInputElement>) => {
                const idx = e.target.value
                setDoNoExists(false)
                setDraft((p) => ({ ...p, doNoIndex: idx, doNo: idx ? `${idx}TNC` : '' }))
              },
              onBlur: () => checkDoNo(draft.doNoIndex),
            })}
          </Box>
          <Box sx={{ gridColumn: { md: 3 }, gridRow: { md: 1 } }}>{roField('doNo', 'Full DO No.')}</Box>
          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 2 } }}>{dateField('doDate', 'DO Date')}</Box>
          <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 2 } }}>
            <Combo label="Sales Contract" value={draft.saleContract} required error={saleContractEmpty}
              options={saleContracts.data?.items ?? []} loading={saleContracts.isFetching} onInputChange={setScQ} labelOnly
              onPick={(v) => set('saleContract', v)} />
          </Box>
          <Box sx={{ gridColumn: { md: 3 }, gridRow: { md: 2 } }}>{roField('todeDelivery', 'To be Delivery', true)}</Box>
          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 3 } }}>{dateField('loadingDate', 'Loading Date')}</Box>
          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 4 } }}>{numField('nwKgs', 'NW kgs')}</Box>
          <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 4 } }}>
            <Combo label="WH Type" value={draft.warehouseType} options={WAREHOUSE_TYPES} labelOnly onPick={(v) => set('warehouseType', v)} />
          </Box>
          <Box sx={{ gridColumn: { md: 3 }, gridRow: { md: 4 } }}>
            <Combo label="From Warehouse" value={draft.fromWarehouse} options={warehouses.data?.items ?? []} loading={warehouses.isFetching} labelOnly onPick={(v) => set('fromWarehouse', v)} />
          </Box>
        </Box>

        {/* ── Block 2: ICO - Marking (T=1584) ── */}
        <Typography sx={sectionTitleSx}>ICO - Marking</Typography>
        <Box sx={grid3Sx}>
          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 1 } }}>
            {textField('icoNoIndex', 'ICO No', {
              inputProps: { style: { textAlign: 'right' }, inputMode: 'numeric' }, // Access Format="0" (integer, căn phải)
              onChange: (e: ChangeEvent<HTMLInputElement>) => {
                const idx = e.target.value
                setDraft((p) => ({ ...p, icoNoIndex: idx, icoNo: idx ? `145/19743/${idx}` : '' }))
              },
            })}
          </Box>
          <Box sx={{ gridColumn: { md: '2 / -1' }, gridRow: { md: 1 } }}>{roField('icoNo', 'Full ICO No.')}</Box>
        </Box>

        {/* ── Block 3: Service (T=1968–4188) ── */}
        <Typography sx={sectionTitleSx}>Service</Typography>
        <Box sx={grid3Sx}>
          {/* Access marking L=3816 col1 H=1128 ≈ 2× field thường → span 2 row (phủ col1
              của row Type of inspection); type_of_inspection giữ col2, inspection_company col3. */}
          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: '1 / span 2' } }}>
            {textField('marking', 'Marking', {
              multiline: true, minRows: 4,
              sx: { height: '100%', '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start' } },
            })}
          </Box>
          <Box sx={{ gridColumn: { md: '2 / -1' }, gridRow: { md: 1 } }}>
            <Combo label="Dunnage" value={draft.dunnage} options={dunnage.data?.items ?? []} loading={dunnage.isFetching} labelOnly minRows={2} onPick={(v) => set('dunnage', v)} />
          </Box>
          <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 2 } }}>
            <Combo label="Type of inspection" value={draft.typeOfInspection} options={inspectionTypes.data?.items ?? []} loading={inspectionTypes.isFetching} labelOnly minRows={2} onPick={(v) => set('typeOfInspection', v)} />
          </Box>
          <Box sx={{ gridColumn: { md: 3 }, gridRow: { md: 2 } }}>
            <ServiceCombo label="Inspection Co." types="inspection" value={draft.inspectionCompany} valueLabel={labels.inspectionCompany} minRows={2} onPick={(v) => set('inspectionCompany', v)} />
          </Box>
          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 3 } }}>
            <ServiceCombo label="Fumigation Co." types="fumigation" value={draft.fumigationCompany} valueLabel={labels.fumigationCompany} minRows={2} onPick={(v) => set('fumigationCompany', v)} />
          </Box>
          <Box sx={{ gridColumn: { md: '2 / -1' }, gridRow: { md: 3 } }}>{textField('fumiDose', 'Fumi Dose', { multiline: true, sx: tallFieldSx(2) })}</Box>
          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 4 } }}>
            <ServiceCombo label="Truck Co." types="trucking" value={draft.truckCompany} valueLabel={labels.truckCompany} onPick={(v) => set('truckCompany', v)} />
          </Box>
          <Box sx={{ gridColumn: { md: '2 / -1' }, gridRow: { md: 4 } }}>{numField('noContTruck', 'No. cont/truck')}</Box>
          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 5 } }}>
            <ServiceCombo label="Customs Code" types="customs" value={draft.customs} valueLabel={labels.customs} codeOnly
              onPick={(v, item) => setDraft((p) => ({ ...p, customs: v, customsName: item?.label ?? '' }))} />
          </Box>
          <Box sx={{ gridColumn: { md: '2 / -1' }, gridRow: { md: 5 } }}>{roField('customsName', 'Customs Name')}</Box>
        </Box>

        {/* ── Block 4: Booking for export (T=4572–8520) ── */}
        <Typography sx={sectionTitleSx}>Booking for export</Typography>
        <Box sx={grid3Sx}>
          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 1 } }}>{textField('bookingNo', 'Booking No')}</Box>
          <Box sx={{ gridColumn: { md: '2 / -1' }, gridRow: { md: 1 } }}>{textField('blNumber', 'B/L number')}</Box>
          {/* Access Text2308 (readonly short_name của freight_payable) bị bỏ → freight combo span lấp ô trống row2. */}
          <Box sx={{ gridColumn: { md: '1 / span 2' }, gridRow: { md: 2 } }}>
            <ServiceCombo label="Freight payer O/B TLC" types="forwarder,shipping_lines" value={draft.freightPayable} valueLabel={labels.freightPayable} onPick={(v) => set('freightPayable', v)} />
          </Box>
          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 3 } }}>
            <ServiceCombo label="Shipping line" types="shipping_lines" value={draft.shippingLine} valueLabel={labels.shippingLine} minRows={2} onPick={(v) => set('shippingLine', v)} />
          </Box>
          <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 3 } }}>
            <Combo label="Vessel Name" value={draft.vesselName} options={vessels.data?.items ?? []} loading={vessels.isFetching} labelOnly minRows={2} onPick={(v) => set('vesselName', v)} />
          </Box>
          <Box sx={{ gridColumn: { md: 3 }, gridRow: { md: 3 } }}>{textField('voyage', 'Voyage', { multiline: true, sx: tallFieldSx(2) })}</Box>
          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 4 } }}>
            <Combo label="Export Terminal" value={draft.exportTerminal} options={exportTerminals.data?.items ?? []} loading={exportTerminals.isFetching} labelOnly onPick={(v) => set('exportTerminal', v)} />
          </Box>
          <Box sx={{ gridColumn: { md: '2 / -1' }, gridRow: { md: 4 } }}>
            <Combo label="Internal Transit Port" value={draft.internalTransshipmentPort} options={internalPorts.data?.items ?? []} loading={internalPorts.isFetching} labelOnly onPick={(v) => set('internalTransshipmentPort', v)} />
          </Box>
          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 5 } }}>
            <PortCombo label="Port of loading" value={draft.portOfLoading} valueLabel={labels.portOfLoading} onPick={(v) => set('portOfLoading', v)} />
          </Box>
          <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 5 } }}>
            <PortCombo label="Transhipment port" value={draft.transhipmentPort} valueLabel={labels.transhipmentPort} onPick={(v) => set('transhipmentPort', v)} />
          </Box>
          <Box sx={{ gridColumn: { md: 3 }, gridRow: { md: 5 } }}>
            <PortCombo label="Destination port" value={draft.destinationPort} valueLabel={labels.destinationPort} onPick={(v) => set('destinationPort', v)} />
          </Box>
          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 6 } }}>{textField('placeOfDelivery', 'Place of delivery', { multiline: true, sx: tallFieldSx(2) })}</Box>
          <Box sx={{ gridColumn: { md: '2 / -1' }, gridRow: { md: 6 } }}>{textField('cutOff', 'Cut off', { multiline: true, sx: tallFieldSx(2) })}</Box>
          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 7 } }}>{dateField('etd', 'ETD')}</Box>
          <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 7 } }}>{dateField('eta', 'ETA')}</Box>
          <Box sx={{ gridColumn: { md: 3 }, gridRow: { md: 7 } }}>{dateField('atd', 'ATD')}</Box>
          {/* Lone multiline note ở row cuối → span full-width lấp ô trống (skill Bước 4). */}
          <Box sx={{ gridColumn: { md: '1 / -1' }, gridRow: { md: 8 } }}>{textField('bookingNote', 'Note', { multiline: true, minRows: 2 })}</Box>
        </Box>

        {/* ── Block 5: Docs Sending (T=9216–9600) ── */}
        <Typography sx={sectionTitleSx}>Docs Sending</Typography>
        <Box sx={grid3Sx}>
          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 1 } }}>{dateField('docsPresentationDate', 'Docs presentation date')}</Box>
          <Box sx={{ gridColumn: { md: '2 / -1' }, gridRow: { md: 1 } }}>
            <Combo label="PIC" value={draft.pic} valueLabel={labels.pic} options={pics.data?.items ?? []} loading={pics.isFetching} onPick={(v) => set('pic', v)} />
          </Box>
          <Box sx={{ gridColumn: { md: '1 / span 2' }, gridRow: { md: 2 } }}>
            <Combo label="Sending docs to" value={draft.sendingDocsTo} valueLabel={labels.sendingDocsTo}
              options={docsDispatch.data?.items ?? []} loading={docsDispatch.isFetching}
              onPick={(v) => setDraft((p) => ({ ...p, sendingDocsTo: v, indication: v ? v.slice(0, 2) : '' }))} />
          </Box>
          <Box sx={{ gridColumn: { md: 3 }, gridRow: { md: 2 } }}>{roField('indication', 'Indication')}</Box>
        </Box>
        </>
        )}
      </DialogContent>
    </Dialog>
  )
}
